import { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '@v2/shared/lib/errors/reportError';

type Props = {
  /** What to draw instead. Given the error so a boundary can say what broke. */
  fallback: (error: Error) => ReactNode;
  children: ReactNode;
};

type State = { error: Error | null };

/**
 * Keeps one bad product from blanking the sale.
 *
 * Without a boundary anywhere, a single malformed row in a several-hundred-product
 * payload throws during render and React unmounts the whole tree — the customer gets a
 * white page on the best-converting page on the site, and the only trace is a console
 * line nobody is reading.
 *
 * Placed around each category rather than around the page, so what is lost is a section
 * and not the sale.
 */
export default class SaleErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('A part of the sale failed to render', error, info.componentStack);
    reportError(error, { source: 'render', component: 'Pages.MegaSale', componentStack: info.componentStack });
  }

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error);
    }

    return this.props.children;
  }
}
